import { useId, useState } from "react";
import type { Row } from "../api";
import {
  addressBinding,
  readStructuredAddress,
  writeStructuredAddress,
  isUSAddressCountry,
  type AddressBinding,
  type AddressComponents,
} from "../address-model";
import {
  AddressAutocomplete,
  type AddressFieldIssue,
} from "./AddressAutocomplete";
import {
  friendlyFieldLabel,
  hasTechnicalDependency,
  parseIntakeNumber,
  resolveIntakeSchema,
} from "../intake-values";
import {
  confirmTaxDesigneeDetails,
  isTaxObject,
  normalizeTaxAge,
  taxAnswerChange,
  taxAnswerView,
  taxChoiceLabel,
  taxDesigneeDetails,
  taxNullableSchema,
  taxObjectChange,
  taxVisibleObjectSchema,
} from "../tax-field-values";
import {
  fieldTargetId,
  validateTaxFields,
  type FieldIssue,
} from "../field-validation";

export interface TaxFieldsProps {
  schema: Row;
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
  disabled?: boolean;
  definitions?: Row;
  sectionId?: string;
  sectionLabel?: string;
  rootId?: string;
  builderMode?: boolean;
  showRequiredErrors?: boolean;
}

/** Natural tax-topic fields. Original persistence contracts and validation stay outside the presentation layer. */
export function TaxFields(props: TaxFieldsProps) {
  const generatedId = useId();
  const rootId = props.rootId ?? generatedId,
    sectionId = props.sectionId ?? "tax-information",
    sectionLabel = props.sectionLabel ?? props.label;
  const issues = validateTaxFields(props.schema, props.value, {
    rootId,
    sectionId,
    sectionLabel,
    rootLabel: props.label,
    definitions: props.definitions,
  })
    .filter(
      (issue) =>
        props.showRequiredErrors !== false || issue.kind !== "required",
    )
    .map((issue) =>
      props.builderMode
        ? {
            ...issue,
            reason: issue.reason.replace(
              "Choose Yes or No. Select Not sure if you need to follow up.",
              "Choose Yes or No using the taxpayer’s records.",
            ),
          }
        : issue,
    );
  if (hasTechnicalDependency(props.schema, props.definitions))
    return (
      <Unavailable
        label={props.label}
        id={fieldTargetId(rootId, [])}
        issues={issues}
      />
    );
  return (
    <TaxControl
      {...props}
      rootId={rootId}
      sectionId={sectionId}
      sectionLabel={sectionLabel}
      issues={issues}
      path={[]}
      depth={0}
    />
  );
}

function FieldErrors({ id, issues }: { id: string; issues: FieldIssue[] }) {
  if (!issues.length) return null;
  return (
    <div id={`${id}-error`} className="tax-field-errors">
      {issues.map((issue) => (
        <p
          key={issue.id}
          className="inline-error"
          style={{ color: "#b42318" }}
          role="alert"
        >
          {issue.sectionLabel} — {issue.fieldLabel}: {issue.reason}
        </p>
      ))}
    </div>
  );
}

function Unavailable({
  label,
  id,
  issues = [],
}: {
  label: string;
  id: string;
  issues?: FieldIssue[];
}) {
  return (
    <div
      id={id}
      tabIndex={-1}
      className="tax-field-unavailable"
      aria-invalid={issues.length > 0}
      aria-describedby={issues.length ? `${id}-error` : undefined}
    >
      <strong>{label}</strong>
      <p>
        This information needs a connected preparation or review step. It cannot
        be entered here yet.
      </p>
      <FieldErrors id={id} issues={issues} />
    </div>
  );
}

interface InternalTaxProps extends TaxFieldsProps {
  depth: number;
  optional?: boolean;
  rootId: string;
  sectionId: string;
  sectionLabel: string;
  path: string[];
  issues: FieldIssue[];
  readOnlyAge?: boolean;
  autoComplete?: string;
}
function TaxControl({
  schema,
  value,
  onChange,
  label,
  disabled = false,
  definitions,
  depth,
  optional = false,
  rootId,
  sectionId,
  sectionLabel,
  path,
  issues,
  readOnlyAge = false,
  autoComplete,
  builderMode = false,
  showRequiredErrors = true,
}: InternalTaxProps) {
  const id = fieldTargetId(rootId, path);
  const [replaceSource, setReplaceSource] = useState<unknown>();
  const localIssues = issues.filter((issue) => issue.targetId === id);
  const error = localIssues.length > 0;
  const errorStyle = error ? { borderColor: "#b42318" } : undefined;
  const errorProps = {
    "aria-invalid": error,
    "aria-describedby": error ? `${id}-error` : undefined,
  };
  const errorHelp = <FieldErrors id={id} issues={localIssues} />;
  const resolved = resolveIntakeSchema(schema, definitions);
  if (!resolved || depth > 24)
    return <Unavailable label={label} id={id} issues={localIssues} />;
  const nextProps = {
    disabled,
    definitions,
    depth: depth + 1,
    rootId,
    sectionId,
    sectionLabel,
    path,
    issues,
    builderMode,
    showRequiredErrors,
  };
  if (resolved["x-intake-stateful"]) {
    const view = taxAnswerView(value);
    if (view.replacementRequired && replaceSource !== value)
      return (
        <div className="tax-field-notice">
          <strong>{label}</strong>
          <p>{view.notice}</p>
          <button
            id={id}
            {...errorProps}
            style={errorStyle}
            className="btn"
            type="button"
            disabled={disabled}
            onClick={() => setReplaceSource(value)}
          >
            {builderMode ? "Update information" : "Replace answer"}
          </button>
          {errorHelp}
        </div>
      );
    return (
      <TaxControl
        {...nextProps}
        schema={resolved["x-intake-stateful"].valueSchema}
        value={view.value}
        label={label}
        onChange={(next) => onChange(taxAnswerChange(next))}
      />
    );
  }

  const nullable = taxNullableSchema(resolved, definitions);
  if (!nullable)
    return <Unavailable label={label} id={id} issues={localIssues} />;
  const field = nullable.schema;
  const hasAge = Boolean(
    field.properties?.date_of_birth && field.properties?.age_for_2025,
  );
  const originalCurrent =
    value === null && nullable.nullable ? undefined : value;
  const current = hasAge ? normalizeTaxAge(originalCurrent) : originalCurrent;
  const nullAction =
    nullable.nullable && field.type !== "null" && !(builderMode && optional) ? (
      <button
        className="tax-not-provided btn"
        type="button"
        disabled={disabled || value === null}
        aria-label={`Mark ${label} as not provided`}
        onClick={() => onChange(null)}
      >
        Not provided
      </button>
    ) : null;

  if (readOnlyAge)
    return (
      <div className="tax-field intake-value-field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type="text"
          readOnly
          value={typeof current === "number" ? String(current) : ""}
          placeholder="Enter date of birth first"
          aria-describedby={`${id}-derived`}
        />
        <small id={`${id}-derived`}>
          Calculated from date of birth for tax year 2025, for a living
          taxpayer. No manual age entry is needed.
        </small>
      </div>
    );

  if (
    field.type === "object" &&
    (isTaxObject(field.properties) || field.allOf || field.if)
  ) {
    const visible = taxVisibleObjectSchema(field, current, definitions);
    if (!visible)
      return <Unavailable label={label} id={id} issues={localIssues} />;
    const designee = taxDesigneeDetails(visible, current, definitions);
    const shownFields = Object.entries(visible.properties).filter(
      ([key]) =>
        !designee.hideDetails ||
        !["designee_name", "designee_phone", "designee_pin"].includes(key),
    );
    if ((optional || nullable.nullable) && !isTaxObject(current))
      return (
        <div className="tax-optional-group">
          <strong>{label}</strong>
          {value === null && <p>Not provided</p>}
          <div className="form-actions">
            <button
              id={id}
              {...errorProps}
              style={errorStyle}
              className="btn"
              type="button"
              disabled={disabled}
              onClick={() => onChange({})}
            >
              Add {label.toLocaleLowerCase("en")}
            </button>
            {nullAction}
          </div>
          {errorHelp}
        </div>
      );
    const binding = addressBinding(visible);
    if (binding)
      return (
        <AddressTaxControl
          {...nextProps}
          schema={visible}
          value={current}
          onChange={onChange}
          label={label}
          binding={binding}
        />
      );
    const confirmationId = fieldTargetId(rootId, [
      ...path,
      "@designee-confirmation",
    ]);
    const confirmationIssues = issues.filter(
      (issue) => issue.targetId === confirmationId,
    );
    return (
      <fieldset
        id={id}
        tabIndex={-1}
        {...errorProps}
        style={errorStyle}
        className={`tax-field-group intake-value-group${error ? " has-field-error" : ""}`}
        disabled={disabled}
      >
        <legend>{label}</legend>
        {shownFields.map(([key, child]) => (
          <TaxControl
            {...nextProps}
            path={[...path, key]}
            key={key}
            schema={child as Row}
            value={isTaxObject(current) ? current[key] : undefined}
            label={
              (child as Row)["x-tax-label"] ??
              (child as Row)["x-intake-label"] ??
              friendlyFieldLabel(key)
            }
            optional={!visible.required.includes(key)}
            readOnlyAge={hasAge && key === "age_for_2025"}
            onChange={(next) => {
              const changed = taxObjectChange(current, key, next);
              onChange(hasAge ? normalizeTaxAge(changed) : changed);
            }}
          />
        ))}
        {designee.hasRetainedDetails && (
          <p className="tax-field-notice">
            Previously entered designee details are retained. They have not been
            cleared.
          </p>
        )}
        {designee.missingNullableFields.length > 0 && (
          <>
            <button
              id={confirmationId}
              aria-invalid={confirmationIssues.length > 0}
              aria-describedby={
                confirmationIssues.length
                  ? `${confirmationId}-error`
                  : undefined
              }
              style={
                confirmationIssues.length
                  ? { borderColor: "#b42318" }
                  : undefined
              }
              className="btn"
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange(
                  confirmTaxDesigneeDetails(current, visible, definitions),
                )
              }
            >
              No designee details to provide
            </button>
            <FieldErrors id={confirmationId} issues={confirmationIssues} />
          </>
        )}
        {nullAction}
        {errorHelp}
      </fieldset>
    );
  }

  if (field.type === "array" && isTaxObject(field.items)) {
    const items = Array.isArray(current) ? current : [];
    const itemLabel = field["x-tax-item-label"] ?? "Item";
    return (
      <fieldset
        id={id}
        tabIndex={-1}
        {...errorProps}
        style={errorStyle}
        className={`tax-field-group intake-value-group${error ? " has-field-error" : ""}`}
        disabled={disabled}
      >
        <legend>{label}</legend>
        {current === undefined && <p>Nothing entered yet.</p>}
        {current !== undefined && !items.length && <p>No items.</p>}
        {items.map((item, index) => (
          <div className="tax-array-item" key={index}>
            <TaxControl
              {...nextProps}
              path={[...path, String(index)]}
              schema={field.items}
              value={item}
              label={`${itemLabel} ${index + 1}`}
              onChange={(next) =>
                onChange(
                  items.map((previous, itemIndex) =>
                    itemIndex === index ? next : previous,
                  ),
                )
              }
            />
            <button
              className="btn"
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange(items.filter((_, itemIndex) => itemIndex !== index))
              }
            >
              Remove {itemLabel.toLocaleLowerCase("en")} {index + 1}
            </button>
          </div>
        ))}
        <div className="form-actions">
          <button
            className="btn"
            type="button"
            disabled={
              disabled ||
              (field.maxItems !== undefined && items.length >= field.maxItems)
            }
            onClick={() => onChange([...items, undefined])}
          >
            Add {itemLabel.toLocaleLowerCase("en")}
          </button>
          {current === undefined && (field.minItems ?? 0) === 0 && (
            <button
              className="btn"
              type="button"
              disabled={disabled}
              onClick={() => onChange([])}
            >
              No items to add
            </button>
          )}
          {nullAction}
        </div>
        {errorHelp}
      </fieldset>
    );
  }

  const options: unknown[] | undefined = Array.isArray(field.enum)
    ? field.enum
    : Object.prototype.hasOwnProperty.call(field, "const")
      ? [field.const]
      : field.type === "boolean"
        ? [true, false]
        : undefined;
  if (
    options &&
    options.every(
      (option) =>
        option === null ||
        ["string", "number", "boolean"].includes(typeof option),
    )
  ) {
    if (options.includes("KNOWN") || options.includes("PENDING_POSTCALC"))
      return <Unavailable label={label} id={id} issues={localIssues} />;
    const unresolved =
      builderMode &&
      ["UNKNOWN", "UNSURE", "CONFLICT"].includes(String(current));
    if ((current === "CONFLICT" || unresolved) && replaceSource !== current)
      return (
        <div className="tax-field-notice">
          <strong>{label}</strong>
          <p>
            {builderMode
              ? "This information needs follow-up. The saved entry is preserved until you provide updated information."
              : "The saved information conflicts. Replace it when you have the correct information."}
          </p>
          <button
            id={id}
            {...errorProps}
            style={errorStyle}
            className="btn"
            type="button"
            disabled={disabled}
            onClick={() => setReplaceSource(current)}
          >
            {builderMode ? "Update information" : "Replace answer"}
          </button>
          {errorHelp}
        </div>
      );
    const selectable = options.filter(
      (option) =>
        option !== "CONFLICT" &&
        !(builderMode && ["UNKNOWN", "UNSURE"].includes(String(option))),
    );
    if (!selectable.length)
      return <Unavailable label={label} id={id} issues={localIssues} />;
    const activeValue =
      current === "CONFLICT" || unresolved ? undefined : current;
    const isScreening = selectable.every((option) =>
      [true, false, "PRESENT", "ABSENT", "YES", "NO", "UNKNOWN"].includes(
        option as never,
      ),
    );
    if (isScreening)
      return (
        <fieldset
          id={id}
          tabIndex={-1}
          {...errorProps}
          style={errorStyle}
          className={`tax-radio-field${error ? " has-field-error" : ""}`}
          disabled={disabled}
        >
          <legend>{label}</legend>
          <div className="tax-radio-options">
            {selectable.map((option, index) => (
              <label key={index} htmlFor={`${id}-${index}`}>
                <input
                  id={`${id}-${index}`}
                  type="radio"
                  name={id}
                  checked={Object.is(activeValue, option)}
                  onChange={() => onChange(option)}
                />
                <span>{taxChoiceLabel(option)}</span>
              </label>
            ))}
          </div>
          {nullAction}
          {errorHelp}
        </fieldset>
      );
    const selected = selectable.findIndex((option) =>
      Object.is(option, activeValue),
    );
    return (
      <div
        className={`tax-field intake-value-field${error ? " has-field-error" : ""}`}
      >
        <label htmlFor={id}>{label}</label>
        <select
          id={id}
          {...errorProps}
          style={errorStyle}
          disabled={disabled}
          value={selected < 0 ? "" : String(selected)}
          onChange={(event) =>
            onChange(
              event.target.value === ""
                ? undefined
                : selectable[Number(event.target.value)],
            )
          }
        >
          <option value="">
            {builderMode ? "Choose…" : "Select an answer"}
          </option>
          {selectable.map((option, index) => (
            <option key={index} value={index}>
              {taxChoiceLabel(option)}
            </option>
          ))}
        </select>
        {nullAction}
        {errorHelp}
      </div>
    );
  }

  if (field.type === "null")
    return (
      <div
        id={value === null ? id : undefined}
        tabIndex={value === null ? -1 : undefined}
        className="tax-field"
      >
        <strong>{label}</strong>
        {value === null ? (
          <p>Not provided</p>
        ) : builderMode && optional ? null : (
          <button
            id={id}
            {...errorProps}
            style={errorStyle}
            className="btn"
            type="button"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            Not provided
          </button>
        )}
        {errorHelp}
      </div>
    );
  if (["string", "number", "integer"].includes(field.type)) {
    const numeric = field.type !== "string";
    const amount =
      field.type === "string" &&
      typeof field.pattern === "string" &&
      field.pattern.includes("[0-9]{2}");
    const hint = amount
      ? "Enter the exact amount, including cents."
      : field.format === "date-time"
        ? "Include the time-zone offset, for example 2025-12-31T17:00:00-08:00."
        : numeric
          ? "Enter the original number. It will not be rounded."
          : "";
    return (
      <div
        className={`tax-field intake-value-field${error ? " has-field-error" : ""}`}
      >
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          {...errorProps}
          style={errorStyle}
          type={field.format === "date" ? "date" : "text"}
          autoComplete={autoComplete}
          disabled={disabled}
          value={
            typeof current === "string" || typeof current === "number"
              ? String(current)
              : ""
          }
          inputMode={numeric || amount ? "decimal" : undefined}
          maxLength={field.maxLength}
          aria-describedby={
            [hint ? `${id}-help` : "", error ? `${id}-error` : ""]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onChange={(event) => {
            const text = event.target.value;
            if (!numeric) onChange(text);
            else {
              const parsed = parseIntakeNumber(text, field.type === "integer");
              onChange(
                parsed.valid ? parsed.value : text === "" ? undefined : text,
              );
            }
          }}
        />
        {nullAction}
        {hint && <small id={`${id}-help`}>{hint}</small>}
        {errorHelp}
      </div>
    );
  }
  return <Unavailable label={label} id={id} issues={localIssues} />;
}

/** Presentation adapter only: persist the existing eight source fields, never provider metadata. */
function AddressTaxControl({
  binding,
  ...props
}: InternalTaxProps & { binding: AddressBinding }) {
  const {
    rootId,
    path,
    value,
    label,
    sectionId,
    sectionLabel,
    onChange,
    disabled,
    schema,
    issues,
  } = props;
  const [postalIssues, setPostalIssues] = useState<AddressFieldIssue[]>([]);
  const [enterForeignCountry, setEnterForeignCountry] = useState(false);
  const addressView = readStructuredAddress(value, binding);
  const components = {
    ...addressView,
    country: isUSAddressCountry(addressView.country)
      ? "US"
      : addressView.country,
  };
  const current = isTaxObject(value) ? value : {};
  const id = fieldTargetId(rootId, path);
  const countryKey = binding.fields.country;
  const rootIssues = issues.filter((issue) => issue.targetId === id);
  const countryId = fieldTargetId(rootId, [...path, countryKey]);
  const isUS = isUSAddressCountry(components.country);
  const foreign = !isUS && (enterForeignCountry || components.country !== "");
  const targets = Object.fromEntries(
    Object.entries(binding.fields)
      .filter(([key]) => !["foreignProvince", "foreignPostal"].includes(key))
      .map(([key, field]) => [key, fieldTargetId(rootId, [...path, field])]),
  ) as Record<keyof AddressComponents, string>;
  const labels: Record<keyof AddressComponents, string> = {
    street: "Street address",
    unit: "Apartment, suite, or unit",
    city: "City",
    state: "State",
    postalCode: "ZIP code",
    country: "Country",
  };
  const providerIssues: FieldIssue[] = postalIssues.map((item) => ({
    id: `${targets[item.field]}-postal`,
    targetId: targets[item.field],
    path: [...path, binding.fields[item.field]],
    sectionId,
    sectionLabel,
    fieldLabel: labels[item.field],
    kind: "invalid",
    reason: item.reason,
  }));
  const allIssues = [...issues, ...providerIssues];
  const countryIssues = allIssues.filter(
    (issue) => issue.targetId === countryId,
  );
  const change = (next: AddressComponents) => {
    setPostalIssues([]);
    onChange(writeStructuredAddress(value, next, binding));
  };
  const child = (key: string, childLabel?: string) => (
    <TaxControl
      {...props}
      key={key}
      depth={props.depth + 1}
      path={[...path, key]}
      schema={
        key === countryKey
          ? (taxNullableSchema(schema.properties[key])?.schema ??
            schema.properties[key])
          : schema.properties[key]
      }
      value={current[key]}
      autoComplete={
        (
          {
            street: "address-line1",
            apartment: "address-line2",
            city: "address-level2",
            state: "address-level1",
            zip: "postal-code",
            foreign_country: "country-name",
            foreign_province: "address-level1",
            foreign_postal: "postal-code",
          } as Record<string, string>
        )[key]
      }
      label={childLabel ?? friendlyFieldLabel(key)}
      issues={allIssues}
      optional={!schema.required?.includes(key)}
      onChange={(next) => {
        setPostalIssues([]);
        onChange(taxObjectChange(value, key, next));
      }}
    />
  );
  if (value !== undefined && !isTaxObject(value))
    return (
      <div
        id={id}
        tabIndex={-1}
        className="tax-field-notice"
        aria-invalid="true"
      >
        <strong>{label}</strong>
        <p>
          The saved address is not structured correctly. It has been preserved.
          Replace it only when the correct address is available.
        </p>
        <FieldErrors id={id} issues={rootIssues} />
        <button
          className="btn"
          type="button"
          disabled={disabled}
          onClick={() => onChange({})}
        >
          Enter replacement address
        </button>
      </div>
    );
  return (
    <fieldset
      id={id}
      tabIndex={-1}
      aria-invalid={rootIssues.length > 0}
      aria-describedby={rootIssues.length ? `${id}-error` : undefined}
      className="tax-field-group intake-value-group address-tax-fields"
      disabled={disabled}
    >
      <legend>{label}</legend>
      <AddressAutocomplete
        id={`${id}-lookup`}
        value={components}
        onChange={change}
        sectionLabel={sectionLabel}
        label={label}
        disabled={disabled}
        fieldTargets={targets}
        onFieldIssues={setPostalIssues}
      >
        <div className="address-manual-grid">
          <div className="address-wide">
            {child(binding.fields.street, "Street address")}
          </div>
          <div className="address-wide">
            {child(binding.fields.unit, "Apartment, suite, or unit")}
          </div>
          {child(binding.fields.city, "City")}
          {child(binding.fields.state, "State")}
          {child(binding.fields.postalCode, "ZIP code")}
          <div
            className={`tax-field intake-value-field${countryIssues.length ? " has-field-error" : ""}`}
          >
            <label htmlFor={foreign ? `${countryId}-choice` : countryId}>
              Country
            </label>
            <select
              autoComplete="country"
              id={foreign ? `${countryId}-choice` : countryId}
              value={isUS ? "US" : foreign ? "OTHER" : ""}
              aria-invalid={countryIssues.length > 0}
              aria-describedby={
                countryIssues.length && !foreign
                  ? `${countryId}-error`
                  : undefined
              }
              style={
                countryIssues.length ? { borderColor: "#b42318" } : undefined
              }
              disabled={disabled}
              onChange={(event) => {
                setPostalIssues([]);
                setEnterForeignCountry(event.target.value === "OTHER");
                if (event.target.value === "US")
                  change({ ...components, country: "US" });
                else onChange(taxObjectChange(value, countryKey, ""));
              }}
            >
              <option value="">Select country</option>
              <option value="US">United States</option>
              <option value="OTHER">Another country — enter below</option>
            </select>
            {!foreign && <FieldErrors id={countryId} issues={countryIssues} />}
          </div>
          {foreign && child(countryKey, "Country name")}
          {(!isUS || current[binding.fields.foreignProvince] !== null) &&
            child(
              binding.fields.foreignProvince,
              "Province outside the United States",
            )}
          {(!isUS || current[binding.fields.foreignPostal] !== null) &&
            child(
              binding.fields.foreignPostal,
              "Postal code outside the United States",
            )}
        </div>
        {isUS &&
          (current[binding.fields.foreignProvince] !== null ||
            current[binding.fields.foreignPostal] !== null) && (
            <button
              type="button"
              className="btn"
              disabled={disabled}
              onClick={() => {
                setPostalIssues([]);
                onChange({
                  ...current,
                  [countryKey]: null,
                  [binding.fields.foreignProvince]: null,
                  [binding.fields.foreignPostal]: null,
                });
              }}
            >
              Confirm United States address
            </button>
          )}
        {foreign && (
          <p className="tax-field-notice">
            Automatic address verification is available for United States
            addresses only. Foreign address entry remains manual; existing
            return requirements still apply.
          </p>
        )}
      </AddressAutocomplete>
      <FieldErrors id={id} issues={rootIssues} />
    </fieldset>
  );
}
